import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useHandleActionItem } from "../../../hooks/useHandleActionItem.js";
import InputText from "../../../components/forms/InputText.jsx";
import { apiUrl } from "../../../config/config.js";

const Toast = Swal.mixin({
	toast: true,
	position: "top-end",
	showConfirmButton: false,
	timer: 3000,
	timerProgressBar: true,
	didOpen: (toast) => {
		toast.onmouseenter = Swal.stopTimer;
		toast.onmouseleave = Swal.resumeTimer;
	},
	background: "#1e2836",
	color: "#fff",
});

const reverseTranslateKeys = (data) => {
	const reverseMap = {
		// Campos en español del backend
		Nombre: "name",
		Descripción: "description",
		Imagen: "image",
		Tipo: "type",
		"Fecha de Inicio": "startDate",
		"Fecha Inicio": "startDate", // Variante sin "de"
		"Fecha de Fin": "endDate",
		"Fecha Fin": "endDate", // Variante sin "de"
		"Fecha Límite de Registro": "registrationDeadline",
		"Límite Registro": "registrationDeadline", // Variante corta
		"Máximo de Participantes": "maxParticipants",
		"Max Participantes": "maxParticipants", // Variante corta
		"Participantes Actuales": "currentParticipants",
		Participantes: "currentParticipants", // Variante corta
		"Cuota de Entrada": "entryFee",
		"Cuota Entrada": "entryFee", // Variante sin "de"
		"Premio Total": "prizePool",
		Estado: "status",
		Reglas: "rules",
		"Premio 1er Lugar": "prizeFirst",
		"1er Premio": "prizeFirst", // Variante corta
		"Premio 2do Lugar": "prizeSecond",
		"2do Premio": "prizeSecond", // Variante corta
		"Premio 3er Lugar": "prizeThird",
		"3er Premio": "prizeThird", // Variante corta
		"Tipo de Cuenta": "accountType",
		ID: "id",
		Creado: "createdAt",
		// Campos que ya vienen en inglés (pasar tal como están)
		accountType: "accountType",
		name: "name",
		description: "description",
		image: "image",
		type: "type",
		startDate: "startDate",
		endDate: "endDate",
		registrationDeadline: "registrationDeadline",
		maxParticipants: "maxParticipants",
		currentParticipants: "currentParticipants",
		entryFee: "entryFee",
		prizePool: "prizePool",
		status: "status",
		rules: "rules",
		prizeFirst: "prizeFirst",
		prizeSecond: "prizeSecond",
		prizeThird: "prizeThird",
		id: "id",
		createdAt: "createdAt",
		updatedAt: "updatedAt",
	};

	const normalized = {};

	for (const key in data) {
		const normalizedKey = reverseMap[key] || key;
		normalized[normalizedKey] = data[key];
	}

	return normalized;
};

function TournamentDetails() {
	const location = useLocation();
	const navigate = useNavigate();
	const locationType = location.state?.type || "";
	const data = useMemo(() => location.state?.data || [], [location.state?.data]);

	const [tournament, setTournament] = useState({
		name: "",
		description: "",
		image: "",
		type: "Weekly Championship",
		accountType: "",
		startDate: "",
		endDate: "",
		registrationDeadline: "",
		maxParticipants: 100,
		currentParticipants: 0,
		entryFee: 0,
		prizePool: 0,
		status: "UPCOMING",
		rules: "",
		prizeFirst: "",
		prizeSecond: "",
		prizeThird: "",
	});

	// Estado separado para el archivo de imagen
	const [imageFile, setImageFile] = useState(null);
	const [imagePreview, setImagePreview] = useState(null);

	const { handleDelete, handleSubmit } = useHandleActionItem();

	useEffect(() => {
		if (locationType === "edit" && data) {
			const tournamentData = reverseTranslateKeys(data);
			// Formatear las fechas para el input datetime-local
			if (tournamentData.startDate) {
				tournamentData.startDate = new Date(tournamentData.startDate).toISOString().slice(0, 16);
			}
			if (tournamentData.endDate) {
				tournamentData.endDate = new Date(tournamentData.endDate).toISOString().slice(0, 16);
			}
			if (tournamentData.registrationDeadline) {
				tournamentData.registrationDeadline = new Date(tournamentData.registrationDeadline).toISOString().slice(0, 16);
			}
			setTournament(tournamentData);
		}
	}, [locationType, data]);

	const validateForm = () => {
		if (!tournament.name || !tournament.startDate || !tournament.endDate || !tournament.registrationDeadline || !tournament.accountType) {
			Toast.fire({
				icon: "error",
				title: "Los campos obligatorios no pueden estar vacíos",
			});
			return false;
		}

		// Validar que las fechas sean coherentes
		const startDate = new Date(tournament.startDate);
		const endDate = new Date(tournament.endDate);
		const registrationDeadline = new Date(tournament.registrationDeadline);

		if (endDate <= startDate) {
			Toast.fire({
				icon: "error",
				title: "La fecha de fin debe ser posterior a la fecha de inicio",
			});
			return false;
		}

		if (registrationDeadline >= startDate) {
			Toast.fire({
				icon: "error",
				title: "La fecha límite de registro debe ser anterior al inicio del torneo",
			});
			return false;
		}

		return true;
	};

	const handleInputChange = (field, value) => {
		setTournament((prev) => ({ ...prev, [field]: value }));
	};

	const handleImageChange = (event) => {
		const file = event.target.files[0];
		if (!file) {
			setImageFile(null);
			setImagePreview(null);
			return;
		}

		// Validar que sea una imagen
		if (!file.type.startsWith("image/")) {
			Toast.fire({
				icon: "error",
				title: "Solo se permiten archivos de imagen",
			});
			event.target.value = ""; // Limpiar input
			return;
		}

		// Validar tamaño (máximo 5MB)
		if (file.size > 5 * 1024 * 1024) {
			Toast.fire({
				icon: "error",
				title: "La imagen no debe superar los 5MB",
			});
			event.target.value = ""; // Limpiar input
			return;
		}

		// Guardar archivo y crear preview
		setImageFile(file);

		// Crear URL de preview para mostrar al usuario
		const reader = new FileReader();
		reader.onload = (e) => {
			setImagePreview(e.target.result);
		};
		reader.readAsDataURL(file);
	};

	const handleSubmitForm = async (event) => {
		event.preventDefault();
		if (!validateForm()) return;

		try {
			// Si hay una imagen nueva, usar FormData para enviar archivo
			if (imageFile) {
				const formData = new FormData();

				// Agregar todos los datos del torneo
				const tournamentData = {
					...tournament,
					startDate: new Date(tournament.startDate).toISOString(),
					endDate: new Date(tournament.endDate).toISOString(),
					registrationDeadline: new Date(tournament.registrationDeadline).toISOString(),
				};

				// Agregar datos como JSON string
				formData.append("action", locationType === "add" ? "create" : "update");
				formData.append("table", "tournaments");
				formData.append("data", JSON.stringify(tournamentData));
				if (locationType === "edit") {
					formData.append("id", tournament.id);
				}

				// Agregar imagen
				formData.append("image", imageFile);
				formData.append("folder", "tournaments");

				// Enviar con FormData
				const response = await fetch(apiUrl + "/actions", {
					method: "POST",
					body: formData, // No agregar Content-Type, FormData lo maneja automáticamente
				});

				const responseData = await response.json();

				if (responseData.ok) {
					Toast.fire({
						icon: "success",
						title: responseData.message,
					});
					navigate("/tournaments");
				} else {
					Toast.fire({
						icon: "error",
						title: responseData.message,
					});
				}
			} else {
				// Si no hay imagen nueva, usar método normal
				const tournamentData = {
					...tournament,
					startDate: new Date(tournament.startDate).toISOString(),
					endDate: new Date(tournament.endDate).toISOString(),
					registrationDeadline: new Date(tournament.registrationDeadline).toISOString(),
				};

				if (locationType === "add") {
					await handleSubmit("add", "tournaments", tournamentData);
				} else {
					await handleSubmit("update", "tournaments", tournamentData);
				}
				navigate("/tournaments");
			}
		} catch (error) {
			console.log("ERROR", error);
			Toast.fire({
				icon: "error",
				title: "Error al guardar el torneo",
			});
		}
	};

	const handleDeleteForm = async (event) => {
		event.preventDefault();
		await handleDelete(tournament.id, "tournaments", true);
	};

	return (
		<div>
			<form onSubmit={handleSubmitForm}>
				<h1 className="text-center">{locationType === "add" ? "Agregar Torneo" : "Actualizar Torneo"}</h1>

				<div className="row">
					<div className="col-md-4">
						<InputText label="Nombre del Torneo" value={tournament.name || ""} setValue={(v) => handleInputChange("name", v)} required />
					</div>
					<div className="col-md-4">
						<div className="mb-3">
							<label className="form-label">Tipo de Torneo</label>
							<select
								className="form-select"
								value={tournament.type || "Weekly Championship"}
								onChange={(e) => handleInputChange("type", e.target.value)}>
								<option value="Weekly Championship">Campeonato Semanal</option>
								<option value="Flash Tournament">Torneo Flash</option>
								<option value="Monthly Championship">Campeonato Mensual</option>
								<option value="Special Event">Evento Especial</option>
							</select>
						</div>
					</div>
					<div className="col-md-4">
						<div className="mb-3">
							<label className="form-label">
								Tipo de cuenta <span className="text-danger">*</span>
							</label>
							<select
								className="form-select"
								value={tournament.accountType || ""}
								onChange={(e) => handleInputChange("accountType", e.target.value)}>
								<option value="">Seleccionar tipo de cuenta</option>
								<option value="demo">Demo</option>
								<option value="real">Real</option>
								<option value="both">Ambos</option>
							</select>
						</div>
					</div>
				</div>

				<div className="mb-3">
					<label className="form-label">Descripción</label>
					<textarea
						className="form-control"
						rows="3"
						value={tournament.description || ""}
						onChange={(e) => handleInputChange("description", e.target.value)}
					/>
				</div>

				<div className="row">
					<div className="col-md-4">
						<div className="mb-3">
							<label className="form-label">Fecha de Inicio</label>
							<input
								type="datetime-local"
								className="form-control"
								value={tournament.startDate || ""}
								onChange={(e) => handleInputChange("startDate", e.target.value)}
								required
							/>
						</div>
					</div>
					<div className="col-md-4">
						<div className="mb-3">
							<label className="form-label">Fecha de Fin</label>
							<input
								type="datetime-local"
								className="form-control"
								value={tournament.endDate || ""}
								onChange={(e) => handleInputChange("endDate", e.target.value)}
								required
							/>
						</div>
					</div>
					<div className="col-md-4">
						<div className="mb-3">
							<label className="form-label">Fecha Límite de Registro</label>
							<input
								type="datetime-local"
								className="form-control"
								value={tournament.registrationDeadline || ""}
								onChange={(e) => handleInputChange("registrationDeadline", e.target.value)}
								required
							/>
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col-md-3">
						<InputText
							label="Máximo de Participantes"
							type="number"
							value={tournament.maxParticipants || ""}
							setValue={(v) => handleInputChange("maxParticipants", parseInt(v))}
						/>
					</div>
					<div className="col-md-3">
						<InputText
							label="Participantes Actuales"
							type="number"
							value={tournament.currentParticipants || ""}
							setValue={(v) => handleInputChange("currentParticipants", parseInt(v))}
							disabled={locationType === "add"}
						/>
					</div>
					<div className="col-md-3">
						<InputText
							label="Cuota de Entrada ($)"
							type="number"
							step="0.01"
							value={tournament.entryFee || ""}
							setValue={(v) => handleInputChange("entryFee", parseFloat(v))}
						/>
					</div>
					<div className="col-md-3">
						<InputText
							label="Premio Total ($)"
							type="number"
							step="0.01"
							value={tournament.prizePool || ""}
							setValue={(v) => handleInputChange("prizePool", parseFloat(v))}
						/>
					</div>
				</div>

				<div className="row">
					<div className="col-md-3">
						<div className="mb-3">
							<label className="form-label">Estado</label>
							<select
								className="form-select"
								value={tournament.status || "UPCOMING"}
								onChange={(e) => handleInputChange("status", e.target.value)}>
								<option value="REGISTRATION">En Registro</option>
								<option value="UPCOMING">Próximo</option>
								<option value="IN_PROGRESS">En Progreso</option>
								<option value="COMPLETED">Completado</option>
								<option value="CANCELLED">Cancelado</option>
							</select>
						</div>
					</div>
					<div className="col-md-9">
						<div className="mb-3">
							<label className="form-label">Imagen del Torneo</label>
							<input type="file" className="form-control" accept="image/*" onChange={(e) => handleImageChange(e)} />
							{/* Vista previa de imagen nueva */}
							{imagePreview && (
								<div className="mt-2">
									<small className="text-success">Nueva imagen seleccionada:</small>
									<div className="mt-1">
										<img
											src={imagePreview}
											alt="Vista previa nueva"
											style={{ maxWidth: "100px", maxHeight: "100px", objectFit: "cover", border: "2px solid #28a745" }}
										/>
									</div>
								</div>
							)}

							{/* Imagen actual (solo si no hay nueva imagen seleccionada) */}
							{!imagePreview && tournament.image && (
								<div className="mt-2">
									<small className="text-muted">Imagen actual:</small>
									<div className="mt-1">
										<img
											src={tournament.image}
											alt="Imagen actual"
											style={{ maxWidth: "100px", maxHeight: "100px", objectFit: "cover" }}
											onError={(e) => {
												e.target.style.display = "none";
											}}
										/>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				<div className="mb-3">
					<label className="form-label">Reglas del Torneo</label>
					<textarea
						className="form-control"
						rows="5"
						value={tournament.rules || ""}
						onChange={(e) => handleInputChange("rules", e.target.value)}
						placeholder="• Balance inicial: $10,000&#10;• Máximo 20 trades por día&#10;• Prohibido usar bots automáticos"
					/>
				</div>

				<div className="row">
					<div className="col-md-4">
						<InputText
							label="Premio 1er Lugar"
							value={tournament.prizeFirst || ""}
							setValue={(v) => handleInputChange("prizeFirst", v)}
							placeholder="50% del premio total"
						/>
					</div>
					<div className="col-md-4">
						<InputText
							label="Premio 2do Lugar"
							value={tournament.prizeSecond || ""}
							setValue={(v) => handleInputChange("prizeSecond", v)}
							placeholder="30% del premio total"
						/>
					</div>
					<div className="col-md-4">
						<InputText
							label="Premio 3er Lugar"
							value={tournament.prizeThird || ""}
							setValue={(v) => handleInputChange("prizeThird", v)}
							placeholder="20% del premio total"
						/>
					</div>
				</div>

				<div className="d-flex justify-content-between mt-4">
					<button type="button" onClick={() => navigate("/tournaments")} className="btn btn-secondary">
						<i className="fa fa-arrow-left me-1"></i> Volver
					</button>
					<div>
						{locationType === "edit" && (
							<button type="button" onClick={handleDeleteForm} className="btn btn-danger me-2">
								<i className="fa fa-trash me-1"></i> Eliminar
							</button>
						)}
						<button type="submit" className="btn btn-primary">
							<i className="fa fa-save me-1"></i> {locationType === "add" ? "Crear Torneo" : "Actualizar Torneo"}
						</button>
					</div>
				</div>
			</form>
		</div>
	);
}

export default TournamentDetails;
